import { ChevronDownIcon, ChevronUpIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Display, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { useState } from 'react';

import { BreakdownRows } from './BreakdownRows';
import { CATEGORY_COLOR } from './constants';
import { CompactCardPadding, DividerLine, Dot, MetricCard, MetricHeaderButton } from './styled';
import { CategoryMetric } from './types';
import { formatCredits, formatNumber } from './utils';

function CategoryCard({ metric }: { metric: CategoryMetric }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const color = CATEGORY_COLOR[metric.key];
  const isExpandable = metric.breakdown.length > 0;

  return (
    <MetricCard data-test-id={`sap-usage-category-card-${metric.key}`}>
      <MetricHeaderButton
        type='button'
        disabled={!isExpandable}
        onClick={() => setIsExpanded((current) => !current)}
        data-test-id={`sap-usage-category-toggle-${metric.key}`}>
        <Dot $color={color} $size={theme.sizes.x12} />
        <Text level='md' weight={theme.fontWeight.medium}>
          {metric.label}
        </Text>
        <StackItem grow='1' />
        {isExpandable &&
          (isExpanded ? (
            <ChevronUpIcon type='default' size='s' color={theme.colors.gray500} />
          ) : (
            <ChevronDownIcon type='default' size='s' color={theme.colors.gray500} />
          ))}
      </MetricHeaderButton>

      <CompactCardPadding gap={theme.spaces.x16}>
        <Stack gap={theme.spaces.x4}>
          <Stack direction='horizontal' gap={theme.spaces.x4} alignY='baseline'>
            <Display level='sm' weight={theme.fontWeight.semiBold}>
              {formatCredits(metric.credits)}
            </Display>
            <Text level='md' tone={theme.textColor.tertiary}>
              CU
            </Text>
          </Stack>
          <Text level='sm' tone={theme.textColor.tertiary}>
            {metric.caption}
          </Text>
        </Stack>

        <DividerLine />

        <Stack direction='horizontal' alignY='center'>
          <Text level='sm' tone={theme.textColor.tertiary}>
            Share of usage
          </Text>
          <StackItem grow='1' />
          <Text level='md' weight={theme.fontWeight.medium} tone={color}>
            {formatNumber(metric.sharePercent, 1)}%
          </Text>
        </Stack>

        {isExpanded && isExpandable && (
          <div data-test-id={`sap-usage-category-breakdown-${metric.key}`}>
            <BreakdownRows rows={metric.breakdown} color={color} />
          </div>
        )}
      </CompactCardPadding>
    </MetricCard>
  );
}

export function CategoryMetricCards({ categories }: { categories: CategoryMetric[] }) {
  return (
    <Stack direction='horizontal' gap={theme.spaces.x20} alignY='start'>
      {categories.map((metric) => (
        <CategoryCard key={metric.key} metric={metric} />
      ))}
    </Stack>
  );
}
